import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import CurrencyInput from "react-currency-input-field";

import styled from "styled-components/macro";

import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";

import FnrAccordion from "UI/FnrAccordion/FnrAccordion";
import FnrChevron from "UI/FnrChevron";
import FnrCheckbox from "UI/FnrEnrichedCheckbox/FnrCheckbox";
import FnrInput2 from "UI/FnrInput2";
import { colors } from "UI/colors";

import AddOnGiftMainContent from "./AddOnGiftMainContent";
import MoreInfoPopover from "./MoreInfoPopover";
import calculateAddOnGiftAmount from "./calculateAddOnGiftAmount";

const mockDescription =
  "Here is a description of the fund. I'm not sure what to say or do here, but I want to make it show up so that I can capture it while I'm writing my code.";

const formatUSD = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

const ctfData = window?.data?.paymentPageData?.ctfData || {};

export default function AddOnGift({ addOnGift }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { addOnDescription } = addOnGift;

  if (!addOnGift) return;

  return (
    <>
      <AddOnGiftMainContent
        addOnGift={addOnGift}
        isExpanded={isExpanded}
        setIsExpanded={setIsExpanded}
      />
      <FnrAccordion
        defaultClosed
        alignContentToLabel
        data-cy="FundItPageAddOnGift_AccordionPanel"
        noActiveMargin
        marginCorrection
        active={isExpanded}
        setActive={setIsExpanded}
      >
        <FundDescription>
          <span>{addOnDescription}</span>
        </FundDescription>
      </FnrAccordion>
    </>
  );
}

AddOnGift.propTypes = {
  addOnGift: PropTypes.object,
};

const Wrapper = styled.div``;

const AddOnContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const TitleAndAmountContainer = styled.div`
  display: flex;
  //justify-content: space-between;
  justify-content: flex-start;
  align-items: center;
  //gap: 4px;
  width: 100%;
  line-height: 22px;
`;

const Icon = styled.div`
  font-size: 22px;
  //font-family: "Font Awesome 5 Pro";
  //font-weight: 400;
  width: 32px;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$fundColorSafe)};
`;

const Title = styled.span`
  flex-grow: 1;
  //display: flex;
  //align-items: center;
  max-width: 235px;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$fundColorSafe)};
  font-weight: 400;
`;

const MoreInfo = styled.i`
  //margin-left: 4px;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  opacity: 0.5;
  transition: all 150ms ease-in-out;

  font-size: 18px;
  font-weight: 600;
  margin-left: 6px;

  ${(p) =>
    !p.disabled &&
    `
    &:hover {
        transform: scale(1.1);
        opacity: 1;
    }

    &:active {
        transform: scale(0.9);
    }
  `}
`;

const MoreInfoChevron = styled(FnrChevron)`
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  opacity: 0.5;
  margin-left: 6px;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$fundColorSafe)};
`;

const MaskedInput = styled(CurrencyInput)`
  box-sizing: border-box;
  max-width: 85px;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  height: 22px;
  font-weight: 600;
  //color: #474747;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$fundColorSafe)};
  border-radius: 10px;
  background: transparent;
  border: none;
  font-variant-numeric: tabular-nums;
  font-family: Montserrat;
  appearance: none;
  padding: 12px;
  margin: 0;

  outline: 1px solid #bec3c7;
  margin: 1px;
  border-radius: 6px;

  -webkit-transition: outline 0.6s;
  transition: outline 0.6s;
  transition: font-size 0.2s;
  transition: margin 0.6s;

  &::-moz-placeholder {
    color: #9a9a9a;
    opacity: 1;
  }
  &::placeholder {
    color: #9a9a9a;
  }
  &:focus {
    outline: 0;
  }
  &:focus-within {
    transition: border 0.2s;
    //transition: margin 0.2s;
    //border: 2px solid #278dc1;
    //margin: 0;
    outline: 2px solid ${(p) => p.$fundColorSafe};
  }
`;

const MoneyInput = styled(FnrInput2)`
  box-sizing: border-box;
  max-width: 85px;
  width: 100%;
  font-size: 16px;
  line-height: 16px;
  height: 22px;
  font-weight: 600;
  //color: #474747;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$fundColorSafe)};
  border-radius: 10px;
  background: transparent;
  border: none;
  font-variant-numeric: tabular-nums;
  font-family: Montserrat;
  appearance: none;
  padding: 14px 12px;
  margin: 0;

  outline: 1px solid #bec3c7;
  margin: 1px;
  border-radius: 6px;

  -webkit-transition: outline 0.6s;
  transition: outline 0.6s;
  transition: font-size 0.2s;
  transition: margin 0.6s;

  margin-right: 4px;

  &::-moz-placeholder {
    color: #9a9a9a;
    opacity: 1;
  }
  &::placeholder {
    color: #9a9a9a;
  }
  &:focus {
    outline: 0;
  }
  &:focus-within {
    transition: border 0.2s;
    //transition: margin 0.2s;
    //border: 2px solid #278dc1;
    //margin: 0;
    outline: 2px solid ${(p) => p.$fundColorSafe};
  }
`;

const Amount = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
  font-family: Montserrat;
  //color: #474747;
  color: ${(p) => (p.$grayedOut ? "#9a9a9a" : p.$fundColorSafe)};
  //margin-right: 14px;
  margin-right: 16px;
`;

const FundTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
`;

const FundDescription = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  padding-left: 42px;
  color: #525251;

  span {
    word-break: break-word;
  }
`;
